import {
  authFetch,
  API_BASE_URL,
} from '@/lib/api-client';
import {
  storageLocationsResponseSchema,
  temperatureHistorySchema,
  type StorageLocationsResponse,
  type TemperatureHistory,
  type AlertConfig,
  type TemperatureThresholds,
} from '../types/storage';
import {
  MOCK_STORAGE_LOCATIONS,
  getMockTemperatureHistory,
} from './storage-fixtures';

const USE_MOCK = true;

export async function getStorageLocations(
  siteId: string
): Promise<StorageLocationsResponse> {
  if (USE_MOCK) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return MOCK_STORAGE_LOCATIONS;
  }

  const url = `${API_BASE_URL}/v1/sites/${siteId}/storage-locations`;
  const response = await authFetch(url);

  if (!response.ok) {
    throw new StorageApiError(
      response.status,
      `Failed to fetch storage locations: ${response.statusText}`
    );
  }

  const data: unknown = await response.json();
  return storageLocationsResponseSchema.parse(data);
}

export async function getTemperatureHistory(
  siteId: string,
  locationId: string,
  hours = 24
): Promise<TemperatureHistory> {
  if (USE_MOCK) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return getMockTemperatureHistory(locationId);
  }

  const url = `${API_BASE_URL}/v1/sites/${siteId}/storage-locations/${locationId}/temperature-history?hours=${hours}`;
  const response = await authFetch(url);

  if (!response.ok) {
    throw new StorageApiError(
      response.status,
      `Failed to fetch temperature history: ${response.statusText}`
    );
  }

  const data: unknown = await response.json();
  return temperatureHistorySchema.parse(data);
}

export async function updateAlertConfig(
  siteId: string,
  locationId: string,
  config: AlertConfig
): Promise<void> {
  if (USE_MOCK) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return;
  }

  const url = `${API_BASE_URL}/v1/sites/${siteId}/storage-locations/${locationId}/alert-config`;
  const response = await authFetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    throw new StorageApiError(
      response.status,
      `Failed to update alert config: ${response.statusText}`
    );
  }
}

export async function updateThresholds(
  siteId: string,
  locationId: string,
  thresholds: TemperatureThresholds
): Promise<void> {
  if (USE_MOCK) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return;
  }

  const url = `${API_BASE_URL}/v1/sites/${siteId}/storage-locations/${locationId}/thresholds`;
  const response = await authFetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(thresholds),
  });

  if (!response.ok) {
    throw new StorageApiError(
      response.status,
      `Failed to update thresholds: ${response.statusText}`
    );
  }
}

export async function acknowledgeAlert(
  siteId: string,
  locationId: string,
  alertId: string
): Promise<void> {
  if (USE_MOCK) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return;
  }

  const url = `${API_BASE_URL}/v1/sites/${siteId}/storage-locations/${locationId}/alerts/${alertId}/acknowledge`;
  const response = await authFetch(url, { method: 'POST' });

  if (!response.ok) {
    throw new StorageApiError(
      response.status,
      `Failed to acknowledge alert: ${response.statusText}`
    );
  }
}

export class StorageApiError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string
  ) {
    super(message);
    this.name = 'StorageApiError';
  }
}
